import { useState, useEffect, Fragment } from "react";
import FleetWebApi from "../Api/FleetWebApi";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import { NavLink } from "react-router-dom";
import Container from "@mui/material/Container";
import { Paper, TableHead } from "@mui/material";
import SimChart from "../Device/SimChart";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Button from "@mui/material/Button";
import OnlineArchiveWarning, {
  isInOnlineArchive,
  useOnlineArchiveConfirm,
} from "./OnlineArchiveWarning";

function SimDataUsage(props) {
  const [simUsage, setSimUsage] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit] = useState(20);
  const [skip, setSkip] = useState(0);
  const [sort, setSort] = useState("stdev");
  const [sortDir, setSortDir] = useState("desc");
  const [weekOffset, setWeekOffset] = useState(0);

  const { confirmIfArchived, archiveDialog } = useOnlineArchiveConfirm();

  // Same window the backend uses for week_offset (FleetEndpoints.get_sim_data_summary)
  const getWeekRange = (offset = weekOffset) => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    end.setDate(end.getDate() - 1 - offset * 7);
    const start = new Date(end);
    start.setDate(start.getDate() - 7);
    return { start, end };
  };

  const getWeekLabel = () => {
    const { start, end } = getWeekRange();
    const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${fmt(start)} – ${fmt(end)}`;
  };

  const handleSortClick = (field) => {
    if (sort === field) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSort(field);
      setSortDir("desc");
    }
    setSkip(0);
  };

  // Moving back into a week that reaches the online archive needs confirmation first
  const goToPrevWeek = () => {
    const newOffset = weekOffset + 1;
    confirmIfArchived(getWeekRange(newOffset).start, () => {
      setWeekOffset(newOffset);
      setSkip(0);
    });
  };

  const goToNextWeek = () => {
    setWeekOffset((w) => w - 1);
    setSkip(0);
  };

  const nextPage = () => {
    setSkip(skip + limit);
  };

  const previousPage = () => {
    setSkip(skip - limit);
  };

  useEffect(() => {
    setIsLoading(true);
    // Archived weeks only get here after the user confirmed the archive prompt
    FleetWebApi.getSimDataUsage(
      limit,
      skip,
      sort,
      sortDir,
      weekOffset,
      isInOnlineArchive(getWeekRange(weekOffset).start)
    )
      .then((response) => {
        setSimUsage(response);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setError(error.message);
        setIsLoading(false);
      });
  }, [limit, skip, sort, sortDir, weekOffset]);

  return (
    <Fragment>
      <h1>SIM Data Usage</h1>
      <p style={{ fontSize: "1rem", marginTop: -8, marginBottom: 4 }}>{getWeekLabel()}</p>
      <Grid container spacing={1} alignItems="center" style={{ marginBottom: 16 }}>
        <Grid item>
          <IconButton onClick={goToPrevWeek} disabled={isLoading} title="Previous week">
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item>
          <IconButton onClick={goToNextWeek} disabled={isLoading || weekOffset === 0} title="Next week">
            <ArrowForwardIcon />
          </IconButton>
        </Grid>
        {weekOffset > 0 && (
          <Grid item>
            <Button variant="outlined" size="small" onClick={() => { setWeekOffset(0); setSkip(0); }} disabled={isLoading}>
              Back to Current Week
            </Button>
          </Grid>
        )}
      </Grid>
      {/* Warn as soon as any part of the selected week is in the archive */}
      <OnlineArchiveWarning date={getWeekRange().start} />
      {archiveDialog}
      {error && <p>Error Loading Data: {error}</p>}
      <Grid container spacing={3}>
        {["stdev", "max", "avg"].map((field) => (
          <Grid item xs={12} md={12} lg={3} key={field}>
            <Button
              onClick={() => handleSortClick(field)}
              color="primary"
              variant="contained"
              disabled={isLoading}
              className={sort === field ? "buttonSuccess" : ""}
              endIcon={
                sort === field
                  ? sortDir === "desc"
                    ? <ArrowDownwardIcon />
                    : <ArrowUpwardIcon />
                  : null
              }
            >
              Sort By {field === "stdev" ? "St Dev" : field === "max" ? "Max" : "Avg"}
            </Button>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3} style={{ marginTop: 10 }}>
        <Grid item xs={12} md={12} lg={12}>
          <Paper>
            <Container>
              <br />

              <Table style={{ tableLayout: "auto" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>ICCID</TableCell>
                    <TableCell>Device Name</TableCell>
                    <TableCell>Average</TableCell>
                    <TableCell>Max</TableCell>
                    <TableCell>St Dev</TableCell>
                    <TableCell>Proxy Count</TableCell>
                    <TableCell>Graph</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {simUsage &&
                    simUsage.map((sim, key) => (
                      <TableRow key={key}>
                        <TableCell>{sim._id}</TableCell>
                        <TableCell>
                          <NavLink
                            to={"/device/" + sim.last_device_id}
                            className="Nav_link"
                          >
                            {sim.last_device_name}
                          </NavLink>
                        </TableCell>
                        <TableCell>{sim.avg.toFixed(2)}</TableCell>
                        <TableCell>{sim.max.toFixed(2)}</TableCell>
                        <TableCell>{sim.stdev.toFixed(2)}</TableCell>
                        <TableCell>
                          {sim.proxies && sim.proxies.length}
                        </TableCell>
                        <TableCell>{<SimChart data={sim.history} />}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              {isLoading && <CircularProgress color="secondary" />}
            </Container>
          </Paper>
          {skip !== 0 && (
            <IconButton onClick={previousPage}>
              <ArrowBackIcon />
            </IconButton>
          )}
          {simUsage.length !== 0 && limit === simUsage.length && (
            <IconButton onClick={nextPage}>
              <ArrowForwardIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </Fragment>
  );
}

export default SimDataUsage;
